import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "@iconify/react";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import {
  formatCurrency,
  generateSettlementWeeks,
} from "../../utils/helper";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import { DAY_MS } from "../../utils/constant";
import SearchableSelect from "../Common/SearchableSelect";
import "../Accounts/ledger-page.css";
import "./summary-report.css";
import SummaryTradesModal from "../model/SummaryTradesModal";

const fmtAmount = (value) => {
  const n = Number(value || 0);
  const abs = formatCurrency(Math.abs(n));
  return n < 0 ? `-${abs}` : `${abs}`;
};

const sumNumeric = (rows, key) =>
  rows.reduce((acc, row) => acc + Number(row?.[key] || 0), 0);

const toCsvCell = (value) => {
  const raw = value == null ? "" : String(value);
  return `"${raw.replace(/"/g, '""')}"`;
};

const DocEyeBtn = ({ className = "", ...props }) => (
  <button
    type="button"
    className={`btn btn-link p-0 border-0 ${className}`}
    aria-label="View Trades"
    title="View Trades"
    {...props}
  >
    <span className="d-inline-flex align-items-center gap-1 position-relative">
      <Icon
        icon="tabler:eye"
        className="icon text-primary-600"
        style={{ fontSize: "1.2rem" }}
      />
    </span>
  </button>
);

const DocFileBtn = ({ className = "", ...props }) => (
  <button
    type="button"
    className={`btn btn-link p-0 border-0 ${className}`}
    aria-label="Generate Invoice"
    title="Generate Invoice"
    {...props}
  >
    <span className="d-inline-flex align-items-center gap-1 position-relative">
      <Icon
        icon="tabler:file-text"
        className="icon text-info-600"
        style={{ fontSize: "1.2rem" }}
      />
    </span>
  </button>
);

const SummaryReportLayer = ({ title = "Summary Report" }) => {
  const { AsyncGetApiCall } = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState({
    search: "",
    pageNo: 1,
    limitVal: 10,
    segment: "",
    broker: "",
    client: "",
    script: "",
    startDateTime: "",
    endDateTime: "",
    settlementWeek: "",
  });
  const [meta, setMeta] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [uiFilters, setUiFilters] = useState({
    segment: "",
    script: "",
    broker: "",
    client: "",
    startDateTime: "",
    endDateTime: "",
    settlementWeek: "",
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTradesModal, setShowTradesModal] = useState(false);
  const [segments, setSegments] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [clients, setClients] = useState([]);
  const settlementWeeks = useMemo(() => generateSettlementWeeks(10), []);

  const searchDebounceRef = useRef(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value.trim(), pageNo: 1 }));
    }, 500),
  );

  useEffect(() => {
    const debounced = searchDebounceRef.current;
    return () => debounced.cancel();
  }, []);

  const fetchSummaryReport = useCallback(async () => {
    setLoading(true);
    const result = await AsyncGetApiCall("/orders/get-summary-report", {
      search: query.search || "",
      pageNo: query.pageNo,
      limitVal: query.limitVal,
      segment: query.segment || "",
      broker: query.broker || "",
      client: query.client || "",
      script: query.script || "",
      startDateTime: query.startDateTime || "",
      endDateTime: query.endDateTime || "",
      settlementWeek: query.settlementWeek || "",
    });

    if (result?.errors) {
      toast.error(result.errors[0]);
      setRows([]);
      setMeta({ total: 0, totalPages: 1, currentPage: 1, globalTotals: {} });
    } else if (result?.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
      setMeta({
        total: result.total ?? 0,
        totalPages: Math.max(1, result.totalPages ?? 1),
        currentPage: result.currentPage ?? query.pageNo,
        globalTotals: result.globalTotals || {},
      });
    } else {
      setRows([]);
      setMeta({ total: 0, totalPages: 1, currentPage: 1, globalTotals: {} });
    }
    setLoading(false);
  }, [
    AsyncGetApiCall,
    query.broker,
    query.client,
    query.endDateTime,
    query.limitVal,
    query.pageNo,
    query.script,
    query.search,
    query.segment,
    query.settlementWeek,
    query.startDateTime,
  ]);

  useEffect(() => {
    fetchSummaryReport();
  }, [fetchSummaryReport]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const [segmentRes, brokerRes, clientRes] = await Promise.all([
        AsyncGetApiCall("/trading-settings/segments/list?status=active"),
        AsyncGetApiCall("/broker"),
        AsyncGetApiCall("/users/my-clients"),
      ]);

      if (segmentRes?.success) setSegments(segmentRes.data || []);
      if (brokerRes?.success) setBrokers(brokerRes.data || []);
      if (clientRes?.success) setClients(clientRes.data || []);
    };
    fetchFilterOptions();
  }, [AsyncGetApiCall]);

  useEffect(() => {
    const fetchScripts = async () => {
      if (!uiFilters.segment) {
        setScripts([]);
        return;
      }
      const scriptRes = await AsyncGetApiCall(
        `/trading-settings/scripts?status=active&limitVal=1000&segment=${uiFilters.segment}`,
      );
      if (scriptRes?.success) setScripts(scriptRes.data || []);
    };
    fetchScripts();
  }, [AsyncGetApiCall, uiFilters.segment]);

  console.log("query", query);
  console.log('uiFilters', uiFilters)

  const handleGenerateInvoice = async (targetUserId) => {
    if (!query.startDateTime || !query.endDateTime) {
      toast.warning(
        "Please apply the filters selecting both the start date and end date to proceed.",
      );
      return;
    }

    try {
      setLoading(true);
      const res = await AsyncGetApiCall(
        `/accounts/generate-invoice?targetUserId=${targetUserId || ""}&from=${query.startDateTime}&to=${query.endDateTime}`,
      );
      if (res?.success && res?.data) {
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.document.write(res.data);
          newWindow.document.close();
        } else {
          toast.error("Please allow pop-ups to view the invoice.");
        }
      } else {
        toast.error(res?.message || "Failed to generate invoice");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while generating the invoice");
    } finally {
      setLoading(false);
    }
  };

  const safePage = Math.min(query.pageNo, Math.max(1, meta.totalPages));
  const pageRows = rows;
  const total = meta.total;
  const totalPages = Math.max(1, meta.totalPages);
  const pageSize = query.limitVal;
  const showingFrom = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = total === 0 ? 0 : Math.min(safePage * pageSize, total);

  const totals = useMemo(
    () => ({
      netMtm: meta.globalTotals?.globalNetMtm || 0,
      totalBrokerage: meta.globalTotals?.globalTotalBrokerage || 0,
      brokersBrokerage: meta.globalTotals?.globalBrokersBrokerage || 0,
      totalMtm: meta.globalTotals?.globalTotalMtm || 0,
      grossMtm: meta.globalTotals?.globalGrossMtm || 0,
      downlineMtm: meta.globalTotals?.globalDownlineMtm || 0,
      uplineMtm: meta.globalTotals?.globalUplineMtm || 0,
      selfMtm: meta.globalTotals?.globalMasterMtm || 0,
    }),
    [meta.globalTotals],
  );

  const pageButtons = useMemo(() => {
    const n = meta.totalPages;
    if (n <= 0) return [];
    const cap = Math.min(n, 5);
    const out = [];
    for (let i = 0; i < cap; i += 1) {
      let pageNum;
      if (n <= 5) pageNum = i + 1;
      else if (safePage <= 3) pageNum = i + 1;
      else if (safePage >= n - 2) pageNum = n - 4 + i;
      else pageNum = safePage - 2 + i;
      out.push(pageNum);
    }
    return out;
  }, [meta.totalPages, safePage]);

  const handleExportCsv = () => {
    if (!rows.length) {
      toast.info("No data available to export");
      return;
    }

    const headers = [
      "Login ID",
      "Client Name",
      "Brokers",
      "Net MTM",
      "Total Brokerage",
      "Brokers Brokerage",
      "Total MTM",
      "Gross MTM",
      "Downline MTM",
      "Upline MTM",
      "Self MTM",
      "Outstanding",
      "Net Position",
    ];

    const csvRows = rows.map((row) => {
      const brokerNames = Array.isArray(row.brokerDetails)
        ? row.brokerDetails
          .map((b) => b?.brokerId?.name || b?.brokerId?.loginId)
          .filter(Boolean)
          .join(", ")
        : "";

      return [
        row.loginId || "",
        row.name || "",
        brokerNames,
        row.netMtm ?? 0,
        row.totalBrokerage ?? 0,
        row.brokersBrokerage ?? 0,
        row.totalMtm ?? 0,
        row.grossMtm ?? 0,
        row.downlineMtm ?? 0,
        row.uplineMtm ?? 0,
        row.masterMtm ?? 0,
        row.masterNetRevenue ?? 0,
        row.adminNetRevenue ?? 0,
        row.outSt ?? 0,
        row.netPos ?? 0,
      ];
    });

    const csv = [
      headers.map(toCsvCell).join(","),
      ...csvRows.map((row) => row.map(toCsvCell).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `summary-report-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-24">
        <div className="d-flex align-items-center gap-2 text-secondary-light">
          <Icon icon="tabler:layout-grid" className="icon text-xl" />
          <Icon icon="tabler:chevron-right" className="icon text-sm" />
          <span className="fw-semibold text-primary-light">{title}</span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm radius-8 px-16 py-8 btn-summary-export-csv"
            onClick={handleExportCsv}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 mb-24 ledger-filter-card">
        <div className="card-body p-24">
          <div className="d-flex flex-wrap align-items-end gap-3 gap-lg-4">
            <div style={{ minWidth: "140px", flex: "1 1 140px" }}>
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Segment
              </label>
              <SearchableSelect
                placeholder="Select segment"
                value={uiFilters.segment}
                onChange={(e) =>
                  setUiFilters((prev) => ({
                    ...prev,
                    segment: e.target.value,
                    script: "",
                  }))
                }
                options={segments.map((s) => ({
                  label: s.displayName || s.name,
                  value: s.name,
                }))}
              />
            </div>
            <div style={{ minWidth: "140px", flex: "1 1 140px" }}>
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Instrument
              </label>
              <SearchableSelect
                placeholder={
                  uiFilters.segment
                    ? "Select script"
                    : "Select Segment First"
                }
                disabled={!uiFilters.segment}
                value={uiFilters.script}
                onChange={(e) =>
                  setUiFilters((prev) => ({ ...prev, script: e.target.value }))
                }
                options={scripts.map((s) => ({
                  label: s.name,
                  value: s.name,
                }))}
              />
            </div>
            <div style={{ minWidth: "140px", flex: "1 1 140px" }}>
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Broker
              </label>
              <SearchableSelect
                placeholder="Select broker"
                value={uiFilters.broker}
                onChange={(e) =>
                  setUiFilters((prev) => ({ ...prev, broker: e.target.value }))
                }
                options={brokers.map((b) => ({
                  label: b.name,
                  value: b._id,
                }))}
              />
            </div>
            <div style={{ minWidth: "140px", flex: "1 1 140px" }}>
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Client
              </label>
              <SearchableSelect
                placeholder="Select user"
                value={uiFilters.client}
                onChange={(e) =>
                  setUiFilters((prev) => ({ ...prev, client: e.target.value }))
                }
                options={clients.map((c) => ({
                  label: `${c.loginId} — ${c.name}`,
                  value: c._id,
                }))}
              />
            </div>
            <div style={{ minWidth: "160px", flex: "1 1 160px" }}>
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Start date-time
              </label>
              <div className="position-relative">
                <input
                  type="datetime-local"
                  className="form-control radius-12 h-40-px pe-40"
                  value={uiFilters.startDateTime}
                  onChange={(e) => {
                    setUiFilters((prev) => ({
                      ...prev,
                      startDateTime: e.target.value,
                    }))

                    // setQuery((prev) => ({
                    //   ...prev,
                    //   pageNo: 1,
                    //   startDateTime: e.target.value,
                    // }))
                  }}
                />
                <Icon
                  icon="tabler:calendar"
                  className="icon position-absolute top-50 end-0 translate-middle-y me-12 text-secondary"
                />
              </div>
            </div>
            <div style={{ minWidth: "160px", flex: "1 1 160px" }}>
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                End date-time
              </label>
              <div className="position-relative">
                <input
                  type="datetime-local"
                  className="form-control radius-12 h-40-px pe-40"
                  value={uiFilters.endDateTime}
                  onChange={(e) => {
                    setUiFilters((prev) => ({
                      ...prev,
                      endDateTime: e.target.value,
                    }))

                    // setQuery((prev) => ({
                    //   ...prev,
                    //   pageNo: 1,
                    //   endDateTime: e.target.value,
                    // }))
                  }}
                />
                <Icon
                  icon="tabler:calendar"
                  className="icon position-absolute top-50 end-0 translate-middle-y me-12 text-secondary"
                />
              </div>
            </div>
            <div style={{ minWidth: "160px", flex: "1 1 160px" }}>
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Settlement week
              </label>
              <SearchableSelect
                placeholder="Select"
                value={uiFilters.settlementWeek}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setUiFilters((prev) => ({
                      ...prev,
                      settlementWeek: "",
                      startDateTime: "",
                      endDateTime: "",
                    }));
                    return;
                  }
                  const selected = settlementWeeks.find(
                    (w) => w.value === value,
                  );
                  setUiFilters((prev) => ({
                    ...prev,
                    settlementWeek: value,
                    startDateTime: selected?.startIso || prev.startDateTime,
                    endDateTime: selected?.endIso || prev.endDateTime,
                  }));
                  // setQuery((prev) => ({
                  //   ...prev,
                  //   pageNo: 1,
                  //   settlementWeek: value,
                  //   startDateTime: selected?.startIso || prev.startDateTime,
                  //   endDateTime: selected?.endIso || prev.endDateTime,
                  // }))
                }}
                options={settlementWeeks.map((w) => ({
                  label: w.label,
                  value: w.value,
                }))}
              />
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 ms-lg-auto">
              <button
                type="button"
                className="btn btn-sm radius-8 px-16 py-10 btn-ledger-apply"
                onClick={() =>
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: 1,
                    segment: uiFilters.segment || "",
                    broker: uiFilters.broker || "",
                    client: uiFilters.client || "",
                    script: uiFilters.script || "",
                    startDateTime: uiFilters.startDateTime || "",
                    endDateTime: uiFilters.endDateTime || "",
                    settlementWeek: uiFilters.settlementWeek || "",
                  }))
                }
              >
                Filter
              </button>
              <button
                type="button"
                className="btn btn-sm radius-8 px-16 py-10 btn-ledger-clear"
                onClick={() => {
                  const cleared = {
                    segment: "",
                    script: "",
                    broker: "",
                    client: "",
                    startDateTime: "",
                    endDateTime: "",
                    settlementWeek: "",
                  };
                  setUiFilters(cleared);
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: 1,
                    segment: "",
                    broker: "",
                    client: "",
                    script: "",
                    startDateTime: "",
                    endDateTime: "",
                    settlementWeek: "",
                  }));
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 ledger-table-card">
        <div className="card-body p-24 pb-0 position-relative" style={{ zIndex: 10 }}>
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-16">
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary-light">Show</span>
              <SearchableSelect
                placeholder="Show"
                value={String(query.limitVal)}
                showSearch={false}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setQuery((prev) => ({ ...prev, limitVal: value, pageNo: 1 }));
                }}
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
              />
            </div>
            <div
              className="position-relative flex-grow-1"
              style={{ maxWidth: "280px" }}
            >
              <Icon
                icon="ion:search-outline"
                className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
              />
              <input
                type="search"
                className="form-control ps-40 h-40-px radius-12"
                placeholder="Search by Login Id, Name"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  searchDebounceRef.current(value);
                }}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive px-16 pb-16">
          <table className="table bordered-table sm-table mb-0 text-nowrap small">
            <thead>
              <tr className="bg-white">
                <th className="fw-bold text-primary-light text-uppercase small">
                  SR. NO.
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Master/Client details
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Net MTM
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Total brokerage
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Brokers brokerage
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Total MTM
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Gross MTM
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Downline MTM
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Upline MTM
                </th>
                <th className="fw-bold text-primary-light text-uppercase small">
                  Self MTM
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={12}
                    className="text-center py-5 text-secondary-light"
                  >
                    No matching entries
                  </td>
                </tr>
              ) : (
                pageRows.map((row, idx) => (
                  <tr key={`${row._id}-${idx}`}>
                    <td className="text-secondary-light">
                      {(safePage - 1) * query.limitVal + idx + 1}
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <div>
                          <p className="fw-semibold text-primary-light mb-0 small">
                            {row.loginId} ({row.name})
                          </p>
                          {row.brokerInfos?.length ? (
                            <p className="text-xs text-secondary mb-0">
                              {row.brokerInfos
                                .map((b) => b.name || b.loginId)
                                .join(", ")}
                            </p>
                          ) : (
                            <p className="text-xs text-secondary mb-0">
                              No Brokers
                            </p>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <DocFileBtn
                            onClick={() => handleGenerateInvoice(row._id)}
                          />
                          <DocEyeBtn
                            onClick={() => {
                              setSelectedUser({
                                _id: row._id,
                                name: row.name,
                                loginId: row.loginId,
                              });
                              setShowTradesModal(true);
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td
                      className={
                        row.netMtm < 0 ? "text-danger" : "text-success"
                      }
                    >
                      {fmtAmount(row.netMtm)}
                    </td>
                    <td className="text-primary-light">
                      {fmtAmount(row.totalBrokerage)}
                    </td>
                    <td className="text-primary-light">
                      {fmtAmount(row.brokersBrokerage)}
                    </td>
                    <td
                      className={
                        row.totalMtm < 0 ? "text-danger" : "text-success"
                      }
                    >
                      {fmtAmount(row.totalMtm)}
                    </td>
                    <td
                      className={
                        row.grossMtm < 0 ? "text-danger" : "text-success"
                      }
                    >
                      {fmtAmount(row.grossMtm)}
                    </td>
                    <td className="text-primary-light">
                      {fmtAmount(row.downlineMtm)}
                    </td>
                    <td className="text-primary-light">
                      {fmtAmount(row.uplineMtm)}
                    </td>
                    <td className="text-primary-light">
                      {fmtAmount(row.selfMtm)}
                    </td>
                  </tr>
                ))
              )}

              <tr className="summary-report-final-row">
                <td colSpan={2}>
                  <div className="d-flex align-items-center justify-content-between px-2">
                    <span className="fw-bold">Final Total</span>
                    <div className="d-flex align-items-center gap-2">
                      {/* <span className="text-primary small fw-semibold">Full Bill</span> */}
                      {/* <DocEyeBtn className="summary-report-total-icon" /> */}
                    </div>
                  </div>
                </td>
                <td
                  className={
                    totals.netMtm < 0 ? "text-danger" : "text-success fw-bold"
                  }
                >
                  {fmtAmount(totals.netMtm)}
                </td>
                <td className="fw-bold">{fmtAmount(totals.totalBrokerage)}</td>
                <td className="fw-bold">
                  {fmtAmount(totals.brokersBrokerage)}
                </td>
                <td
                  className={
                    totals.totalMtm < 0 ? "text-danger" : "text-success fw-bold"
                  }
                >
                  {fmtAmount(totals.totalMtm)}
                </td>
                <td
                  className={
                    totals.grossMtm < 0 ? "text-danger" : "text-success fw-bold"
                  }
                >
                  {fmtAmount(totals.grossMtm)}
                </td>
                <td className="fw-bold">{fmtAmount(totals.downlineMtm)}</td>
                <td className="fw-bold">{fmtAmount(totals.uplineMtm)}</td>
                <td className="fw-bold">{fmtAmount(totals.selfMtm)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="border-top d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-24">
          <p className="mb-0 text-secondary-light text-sm">
            Showing{" "}
            <span className="fw-medium text-primary-light">
              {showingFrom} to {showingTo} of {total} entries
            </span>
          </p>
          {total > 0 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() =>
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: Math.max(1, safePage - 1),
                  }))
                }
                disabled={safePage <= 1}
              >
                <Icon icon="tabler:chevron-left" className="icon" />
              </button>
              {pageButtons.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center ${pageNum === safePage
                    ? "ledger-pagination-btn active-page-num"
                    : "bg-base text-secondary-light"
                    }`}
                  onClick={() =>
                    setQuery((prev) => ({ ...prev, pageNo: pageNum }))
                  }
                >
                  {pageNum}
                </button>
              ))}
              {meta.totalPages > 5 && safePage < meta.totalPages - 2 && (
                <>
                  <span className="text-secondary-light px-1">…</span>
                  <button
                    type="button"
                    className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center bg-base text-secondary-light"
                    onClick={() =>
                      setQuery((prev) => ({ ...prev, pageNo: totalPages }))
                    }
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() =>
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: Math.min(totalPages, prev.pageNo + 1),
                  }))
                }
                disabled={safePage >= totalPages}
              >
                <Icon icon="tabler:chevron-right" className="icon" />
              </button>
            </div>
          )}
        </div>
      </div>

      <SummaryTradesModal
        show={showTradesModal}
        onHide={() => setShowTradesModal(false)}
        userId={selectedUser?._id}
        userName={`${selectedUser?.loginId} - ${selectedUser?.name}`}
        startDateTime={query.startDateTime}
        endDateTime={query.endDateTime}
        segment={query.segment}
      />
    </>
  );
};

export default SummaryReportLayer;
